import { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { CreditCard, Loader2, UserPlus, X } from 'lucide-react';
import type { Member, MemberHousehold, MembershipAddon, MembershipType } from '@court-sync/shared';
import {
  Button,
  Checkbox,
  Drawer,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import {
  clubMembersService,
  type MembershipAddonSelectionInput,
  type MembershipPurchaseResponse,
  type MembershipQuoteResponse,
  type MembershipSeatInput,
} from '@/services/clubMembers';
import { formatCurrency, type Currency } from '@/utils/currency';
import { STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { useStripeElementsAppearance } from '@/hooks/useStripeElementsAppearance';

const ASSIGN_FORM_ID = 'assign-membership-form';
const NO_HOUSEHOLD_VALUE = '__none__';

type OfflinePaymentMethod = 'cash' | 'eftpos' | 'bank_transfer' | 'other';

const OFFLINE_PAYMENT_METHODS: { value: OfflinePaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'eftpos', label: 'EFTPOS' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'other', label: 'Other' },
];

interface AssignMembershipDrawerProps {
  clubId: string;
  currency: string;
  types: MembershipType[];
  addons: MembershipAddon[];
  members: Member[];
  households: MemberHousehold[];
  canManageClubPayments: boolean;
  isOpen: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

function formatCentsAsMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function seatLabel(seat: MembershipSeatInput): string {
  return seat.kind === 'existing' ? '' : `${seat.firstName} ${seat.lastName}`;
}

export function AssignMembershipDrawer({
  clubId,
  currency,
  types,
  addons,
  members,
  households,
  canManageClubPayments,
  isOpen,
  onClose,
  onAssigned,
}: AssignMembershipDrawerProps) {
  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [householdId, setHouseholdId] = useState('');
  const [seats, setSeats] = useState<MembershipSeatInput[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);

  const [memberSearch, setMemberSearch] = useState('');
  const [showNewPersonForm, setShowNewPersonForm] = useState(false);
  const [newPersonFirstName, setNewPersonFirstName] = useState('');
  const [newPersonLastName, setNewPersonLastName] = useState('');
  const [newPersonDob, setNewPersonDob] = useState('');

  const [quote, setQuote] = useState<MembershipQuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteDetails, setQuoteDetails] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'build' | 'offline' | 'stripe'>('build');
  const [offlineMethod, setOfflineMethod] = useState<OfflinePaymentMethod>('cash');
  const [offlineNote, setOfflineNote] = useState('');
  const [purchase, setPurchase] = useState<MembershipPurchaseResponse | null>(null);

  const resetForm = () => {
    setMembershipTypeId('');
    setHouseholdId('');
    setSeats([]);
    setSelectedAddonIds([]);
    setMemberSearch('');
    setShowNewPersonForm(false);
    setNewPersonFirstName('');
    setNewPersonLastName('');
    setNewPersonDob('');
    setQuote(null);
    setQuoteError(null);
    setQuoteDetails([]);
    setStep('build');
    setOfflineMethod('cash');
    setOfflineNote('');
    setPurchase(null);
  };

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen]);

  const selectedPlan = types.find((t) => t.id === membershipTypeId) ?? null;

  const existingMemberIdsInSeats = useMemo(
    () => new Set(seats.filter((s) => s.kind === 'existing').map((s) => (s as { memberId: string }).memberId)),
    [seats]
  );

  const memberResults = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return [];
    return members
      .filter((m) => !existingMemberIdsInSeats.has(m.id))
      .filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(query))
      .slice(0, 8);
  }, [memberSearch, members, existingMemberIdsInSeats]);

  const addonSelectionsPayload: MembershipAddonSelectionInput[] = useMemo(
    () => selectedAddonIds.map((addonId) => ({ addonId })),
    [selectedAddonIds]
  );

  // Live quote, debounced — re-fetches whenever plan, seats, or add-ons change.
  useEffect(() => {
    if (!membershipTypeId || seats.length === 0) {
      setQuote(null);
      setQuoteError(null);
      setQuoteDetails([]);
      return undefined;
    }

    setQuoteLoading(true);
    const timer = window.setTimeout(async () => {
      const res = await clubMembersService.quoteMembership(clubId, {
        membershipTypeId,
        seats,
        addonSelections: addonSelectionsPayload,
      });
      if (res.success && res.data) {
        setQuote(res.data);
        setQuoteError(null);
        setQuoteDetails([]);
      } else {
        setQuote(null);
        setQuoteError(res.error || 'Failed to price this membership');
        setQuoteDetails((res as { details?: string[] }).details ?? []);
      }
      setQuoteLoading(false);
    }, 400);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, membershipTypeId, JSON.stringify(seats), JSON.stringify(addonSelectionsPayload)]);

  const addExistingMember = (member: Member) => {
    setSeats((prev) => [...prev, { kind: 'existing', memberId: member.id }]);
    setMemberSearch('');
  };

  const addNewPerson = () => {
    if (!newPersonFirstName.trim() || !newPersonLastName.trim()) return;
    setSeats((prev) => [
      ...prev,
      {
        kind: 'new',
        firstName: newPersonFirstName.trim(),
        lastName: newPersonLastName.trim(),
        dateOfBirth: newPersonDob || null,
      },
    ]);
    setNewPersonFirstName('');
    setNewPersonLastName('');
    setNewPersonDob('');
    setShowNewPersonForm(false);
  };

  const removeSeat = (index: number) => {
    setSeats((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds((prev) =>
      prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId]
    );
  };

  const buildRequest = () => ({
    membershipTypeId,
    seats,
    addonSelections: addonSelectionsPayload,
    householdId: householdId || null,
  });

  const canSubmit = !!quote && !quoteError && seats.length > 0;

  const handleAssignUnpaid = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await clubMembersService.assignMembership(clubId, buildRequest());
      if (res.success) {
        toast.success('Membership assigned (unpaid)');
        onAssigned();
        onClose();
      } else {
        toast.error(res.error || 'Failed to assign membership');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordOfflinePayment = async () => {
    setSubmitting(true);
    try {
      const assignRes = await clubMembersService.assignMembership(clubId, buildRequest());
      if (!assignRes.success || !assignRes.data) {
        toast.error(assignRes.error || 'Failed to assign membership');
        return;
      }
      const paymentRes = await clubMembersService.recordOfflinePayment(clubId, {
        membershipId: assignRes.data.membershipId,
        paymentMethod: offlineMethod,
        note: offlineNote.trim() || undefined,
      });
      if (paymentRes.success) {
        toast.success('Payment recorded');
        onAssigned();
        onClose();
      } else {
        toast.error(paymentRes.error || 'Membership was assigned, but recording payment failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartStripePayment = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await clubMembersService.purchaseMembership(clubId, buildRequest());
      if (res.success && res.data) {
        setPurchase(res.data);
        setStep('stripe');
      } else {
        toast.error(res.error || 'Failed to start payment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripeSuccess = () => {
    toast.success('Payment collected');
    onAssigned();
    onClose();
  };

  const footer =
    step === 'build' ? (
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {canManageClubPayments ? (
          <Button type="button" variant="outline" disabled={!canSubmit || submitting} onClick={() => setStep('offline')}>
            Record offline payment
          </Button>
        ) : null}
        <Button type="button" variant="outline" disabled={!canSubmit || submitting} onClick={handleAssignUnpaid}>
          Assign (unpaid)
        </Button>
        {canManageClubPayments ? (
          <Button type="button" disabled={!canSubmit || submitting} isLoading={submitting} onClick={handleStartStripePayment}>
            <CreditCard className="h-4 w-4" aria-hidden />
            Take payment now
          </Button>
        ) : null}
      </div>
    ) : step === 'offline' ? (
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => setStep('build')}>
          Back
        </Button>
        <Button type="button" isLoading={submitting} onClick={handleRecordOfflinePayment}>
          Confirm payment received
        </Button>
      </div>
    ) : null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Assign membership"
      description="Pick a plan, choose who is covered, and either assign now or collect payment."
      width="2xl"
      footer={footer}
    >
      {step === 'stripe' && purchase ? (
        <MembershipStripeConfirm
          purchase={purchase}
          currency={selectedPlan?.currency ?? currency}
          onSuccess={handleStripeSuccess}
          onCancel={() => setStep('build')}
        />
      ) : step === 'offline' ? (
        <div className="space-y-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This assigns the membership and immediately marks it paid via the method below.
          </p>
          <Field>
            <FieldLabel htmlFor="offline-payment-method">Payment method</FieldLabel>
            <Select value={offlineMethod} onValueChange={(v) => setOfflineMethod(v as OfflinePaymentMethod)}>
              <SelectTrigger id="offline-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OFFLINE_PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="offline-payment-note">Note (optional)</FieldLabel>
            <Input
              id="offline-payment-note"
              placeholder="e.g. receipt number"
              value={offlineNote}
              onChange={(e) => setOfflineNote(e.target.value)}
            />
          </Field>
          {quote ? (
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Amount: {formatCentsAsMoney(quote.breakdown.totalCents, selectedPlan?.currency ?? currency)}
            </p>
          ) : null}
        </div>
      ) : (
        <form id={ASSIGN_FORM_ID} className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          <Field>
            <FieldLabel htmlFor="assign-membership-plan">Membership plan</FieldLabel>
            <Select value={membershipTypeId} onValueChange={setMembershipTypeId}>
              <SelectTrigger id="assign-membership-plan">
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name} — {formatCentsAsMoney(type.priceCents, type.currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel htmlFor="assign-membership-household">Household (optional)</FieldLabel>
            <Select
              value={householdId || NO_HOUSEHOLD_VALUE}
              onValueChange={(v) => setHouseholdId(v === NO_HOUSEHOLD_VALUE ? '' : v)}
            >
              <SelectTrigger id="assign-membership-household">
                <SelectValue placeholder="No household" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_HOUSEHOLD_VALUE}>No household</SelectItem>
                {households.map((household, index) => (
                  <SelectItem key={household.id} value={household.id}>
                    {household.name?.trim() ? household.name : `Household ${index + 1}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Covered people</h3>

            {seats.length > 0 ? (
              <ul className="space-y-1">
                {seats.map((seat, index) => {
                  const resolved = quote?.seats[index];
                  const existingMember = seat.kind === 'existing' ? members.find((m) => m.id === seat.memberId) : null;
                  const name = seat.kind === 'existing' ? `${existingMember?.firstName ?? '…'} ${existingMember?.lastName ?? ''}` : seatLabel(seat);
                  return (
                    <li
                      key={index}
                      className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                    >
                      <span className="text-gray-900 dark:text-white">
                        {name}
                        {resolved ? (
                          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                            {resolved.seatType}
                          </span>
                        ) : null}
                        {index === 0 ? (
                          <span className="ml-2 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                            Primary
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSeat(index)}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        aria-label={`Remove ${name}`}
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No one covered yet — add at least one person.</p>
            )}

            <div className="relative">
              <Input
                placeholder="Search existing members…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              {memberSearch.trim() ? (
                <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-900">
                  {memberResults.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No members match.</li>
                  ) : (
                    memberResults.map((m) => (
                      <li key={m.id}>
                        <button
                          type="button"
                          className="flex w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => addExistingMember(m)}
                        >
                          {m.firstName} {m.lastName}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </div>

            {showNewPersonForm ? (
              <div className="space-y-3 rounded-md border border-gray-200 p-3 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="First name"
                    value={newPersonFirstName}
                    onChange={(e) => setNewPersonFirstName(e.target.value)}
                  />
                  <Input
                    placeholder="Last name"
                    value={newPersonLastName}
                    onChange={(e) => setNewPersonLastName(e.target.value)}
                  />
                </div>
                <Input
                  type="date"
                  value={newPersonDob}
                  onChange={(e) => setNewPersonDob(e.target.value)}
                  aria-label="Date of birth (optional)"
                />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowNewPersonForm(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!newPersonFirstName.trim() || !newPersonLastName.trim()}
                    onClick={addNewPerson}
                  >
                    Add person
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => setShowNewPersonForm(true)}>
                <UserPlus className="h-4 w-4" aria-hidden />
                Add a new person
              </Button>
            )}
          </div>

          {addons.length > 0 ? (
            <div className="space-y-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add-ons</h3>
              <div className="space-y-2">
                {addons.map((addon) => (
                  <label key={addon.id} className="flex cursor-pointer items-start gap-2 text-sm">
                    <Checkbox
                      checked={selectedAddonIds.includes(addon.id)}
                      onCheckedChange={() => toggleAddon(addon.id)}
                    />
                    <span>
                      <span className="font-medium text-gray-900 dark:text-white">{addon.name}</span>{' '}
                      <span className="text-gray-500 dark:text-gray-400">
                        (+{formatCentsAsMoney(addon.priceCents, addon.currency)}
                        {addon.pricingMode === 'per_adult' ? ' / adult' : ''})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Price</h3>
            {quoteLoading ? (
              <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Calculating…
              </p>
            ) : quoteError ? (
              <div className="space-y-1">
                <p className="text-sm text-red-600 dark:text-red-400">{quoteError}</p>
                {quoteDetails.map((detail, i) => (
                  <p key={i} className="text-xs text-red-500 dark:text-red-400">
                    {detail}
                  </p>
                ))}
              </div>
            ) : quote ? (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Base price</span>
                  <span>{formatCentsAsMoney(quote.breakdown.basePriceCents, selectedPlan?.currency ?? currency)}</span>
                </div>
                {quote.breakdown.addonsTotalCents > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Add-ons</span>
                    <span>{formatCentsAsMoney(quote.breakdown.addonsTotalCents, selectedPlan?.currency ?? currency)}</span>
                  </div>
                ) : null}
                {quote.breakdown.surchargeCents > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Processing fee</span>
                    <span>{formatCentsAsMoney(quote.breakdown.surchargeCents, selectedPlan?.currency ?? currency)}</span>
                  </div>
                ) : null}
                <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold dark:border-gray-700">
                  <span>Total</span>
                  <span>{formatCentsAsMoney(quote.breakdown.totalCents, selectedPlan?.currency ?? currency)}</span>
                </div>
                {quote.requiresVerification ? (
                  <p className="pt-1 text-xs text-amber-600 dark:text-amber-400">
                    This membership will require staff verification before it becomes active
                    {quote.verificationReasons.length > 0 ? `: ${quote.verificationReasons.join(', ')}` : '.'}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select a plan and at least one covered person to see pricing.
              </p>
            )}
          </div>
        </form>
      )}
    </Drawer>
  );
}

/** Renders inside <Elements options={{ clientSecret }}> — confirms the PaymentIntent for this membership. */
function MembershipStripeConfirmInner({
  totalCents,
  currency,
  onSuccess,
  onCancel,
}: {
  totalCents: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please check the payment details.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}${window.location.pathname}` },
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading} className="flex-1">
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={loading || !stripe} isLoading={loading} className="flex-1">
          {!loading && (
            <>
              <CreditCard className="mr-2 h-4 w-4" aria-hidden />
              Charge {formatCentsAsMoney(totalCents, currency)}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function MembershipStripeConfirm({
  purchase,
  currency,
  onSuccess,
  onCancel,
}: {
  purchase: MembershipPurchaseResponse;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { appearance } = useStripeElementsAppearance();
  const stripePromise = useMemo(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: purchase.stripeConnectAccountId });
  }, [purchase.stripeConnectAccountId]);

  if (!STRIPE_PUBLISHABLE_KEY || !purchase.clientSecret || !stripePromise) {
    return (
      <p className="text-sm text-red-600 dark:text-red-400">
        Stripe is not configured. Please check your environment variables.
      </p>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: purchase.clientSecret, appearance }}>
      <MembershipStripeConfirmInner
        totalCents={purchase.totalCents}
        currency={currency}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}
